import React, { useEffect, useState } from 'react';
import {
  collection,
  doc,
  getDocs,
  updateDoc,
  deleteDoc,
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuthContext } from '../../context/AuthContext';
import { GeneralEventData, EventType } from '../../types/EventTypes';
import EventWizard from './EventWizard';
import ConfirmModal from '../ConfirmModal'; // ✅ Import your modal
import { usePageTitle } from '../../hooks/usePageTitle';

interface EventItem extends GeneralEventData {
  id: string;
  cancelled?: boolean;
  type: EventType;
  eventName: string;
}

const typeColors: Record<EventType, string> = {
  league: 'bg-blue-600',
  friendly: 'bg-yellow-500',
  training: 'bg-green-600',
  tournament: 'bg-purple-600',
};

const EventList: React.FC = () => {
  usePageTitle('Schedule | From the Pitchside');
  const { user } = useAuthContext();
  const [events, setEvents] = useState<EventItem[]>([]);
  const [showWizard, setShowWizard] = useState(false);
  const [editingEvent, setEditingEvent] = useState<EventItem | null>(null);
  const [eventToDelete, setEventToDelete] = useState<EventItem | null>(null);
  const [eventToCancel, setEventToCancel] = useState<EventItem | null>(null);

  const fetchEvents = async () => {
    if (!user?.teamId || !user?.clubId) return;
    const snap = await getDocs(collection(db, `clubs/${user.clubId}/teams/${user.teamId}/events`));
    const items = snap.docs.map(doc => {
      const data = doc.data();
      const rawDate = data.date;

      return {
        ...(data as EventItem),
        id: doc.id,
        type: data.type || data.eventType,
        date: rawDate?.toDate ? rawDate.toDate().toISOString().slice(0, 10) : rawDate, // ensure string
      };
    });

    setEvents(
      items
        .filter(e => !!e.date) // only keep events with a date
        .sort((a, b) => a.date!.localeCompare(b.date!))
    );      
  };

  useEffect(() => {
    fetchEvents();
  }, [user?.teamId]);

  const handleConfirmDelete = async () => {
    if (!user?.teamId || !eventToDelete?.id) return;
    await deleteDoc(doc(db, 'clubs', user.clubId, 'teams', user.teamId, 'events', eventToDelete.id));
    setEvents(prev => prev.filter(e => e.id !== eventToDelete.id));
    setEventToDelete(null);
  };

  const handleConfirmCancel = async () => {
    if (!user?.teamId || !eventToCancel?.id) return;
    await updateDoc(doc(db, 'clubs', user.clubId, 'teams', user.teamId, 'events', eventToCancel.id), {
      cancelled: true,
    });
    setEvents(prev =>
      prev.map(e => (e.id === eventToCancel.id ? { ...e, cancelled: true } : e))
    );
    setEventToCancel(null);
  };

  const toggleCancelled = async (event: EventItem) => {
    if (!user?.teamId) return;
    const ref = doc(db, `users/${user.clubId}/teams/${user.teamId}/events`, event.id);
    const newStatus = !event.cancelled;
    await updateDoc(ref, { cancelled: newStatus });
    setEvents(prev =>
      prev.map(e => (e.id === event.id ? { ...e, cancelled: newStatus } : e))
    );
  };

  const getTypeLabel = (type: string | undefined): string =>
    ['league', 'friendly', 'training', 'tournament'].includes(type || '')
      ? type!.toUpperCase()
      : 'UNKNOWN';

  return (
    <div className='p-4 text-darkblue'>
      <div className='flex justify-between items-center mb-4'>
        <h2 className='text-2xl font-bold'>Upcoming Events</h2>
        <button
          onClick={() => {
            setEditingEvent(null);
            setShowWizard(true);
          }}
          className='px-4 py-2 bg-blue-600 text-white rounded'
        >
          + Add Event
        </button>
      </div>

      {showWizard && (
        <div className='mb-6'>
          <EventWizard
            onSaved={() => {
              setShowWizard(false);
              setEditingEvent(null);
              fetchEvents();
            }}
            existingEvent={editingEvent || undefined}
          />
        </div>
      )}

      {events.length === 0 && !showWizard && (
        <p className='text-center text-gray-600 italic mt-10'>
          No upcoming events found. Click "Add Event" to get started.
        </p>
      )}

      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
        {events.map(event => (
          <div
            key={event.id}
            className={`relative border p-4 rounded shadow bg-white ${
              event.cancelled ? 'opacity-50' : ''
            }`}
          >
            <span
              className={`absolute top-2 right-2 text-xs text-white px-2 py-1 rounded-full font-semibold ${
                typeColors[event.type as EventType] || 'bg-gray-400'
              }`}
            >
              {getTypeLabel(event.type)}
            </span>

            <h3 className='text-lg font-bold mb-1'>
              {event.eventName || 'No Title'}
            </h3>
            <p className='text-sm text-gray-600'>
              {event.date} | {event.startTime}
            </p>
            <p className='text-sm text-gray-600 mb-2'>{event.location}</p>

            {event.location && (
              <iframe
                className='w-full h-32 rounded border'
                loading='lazy'
                src={`https://maps.google.com/maps?q=${encodeURIComponent(
                  event.location
                )}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
              ></iframe>
            )}

            <div className='flex justify-between items-center mt-3 flex-wrap gap-2'>
              <button
                onClick={() => toggleCancelled(event)}
                className={`px-3 py-1 rounded text-sm ${
                  event.cancelled
                    ? 'bg-gray-400 text-white'
                    : 'bg-yellow-600 text-white'
                }`}
              >
                {event.cancelled ? 'Restore' : 'Cancel'}
              </button>

              <button
                disabled={event.cancelled}
                onClick={() => {
                  setEditingEvent(event);
                  setShowWizard(true);
                }}
                className={`px-3 py-1 text-white text-sm rounded ${
                  event.cancelled
                    ? 'bg-gray-300 cursor-not-allowed'
                    : 'bg-blue-500'
                }`}
              >
                Edit
              </button>

              <button
                disabled={event.cancelled}
                onClick={() => setEventToDelete(event)}
                className={`px-3 py-1 text-white text-sm rounded ${
                  event.cancelled
                    ? 'bg-gray-300 cursor-not-allowed'
                    : 'bg-red-600'
                }`}
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Confirm Cancel */}
      {eventToCancel && (
        <ConfirmModal
          title='Cancel Event'
          message={`Are you sure you want to mark "${
            eventToCancel.eventName || eventToCancel.type
          }" as cancelled?`}
          onCancel={() => setEventToCancel(null)}
          onConfirm={handleConfirmCancel}
        />
      )}

      {/* Confirm Delete */}
      {eventToDelete && (
        <ConfirmModal
          title='Delete Event'
          message={`Are you sure you want to permanently delete "${
            eventToDelete.eventName || eventToDelete.type
          }"?`}
          onCancel={() => setEventToDelete(null)}
          onConfirm={handleConfirmDelete}
        />
      )}
    </div>
  );
};

export default EventList;
